<template>
  <v-form ref="form" v-model="valid" @submit.prevent="save">
    <v-row dense>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="form.invoiceNumber"
          label="N° de factura *"
          :rules="invoiceNumberRules"
          outlined
          dense
          counter="20"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="form.invoiceIssuedAt"
          label="Fecha de emisión *"
          type="date"
          :max="today"
          :rules="issuedAtRules"
          outlined
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <v-file-input
          v-model="form.pdf"
          :label="hasPdf ? 'Reemplazar PDF (opcional)' : 'Factura PDF (opcional)'"
          accept=".pdf,application/pdf"
          :rules="fileRules"
          prepend-icon=""
          prepend-inner-icon="mdi-file-pdf-box"
          outlined
          dense
          show-size
        ></v-file-input>
      </v-col>
      <v-col cols="12" sm="6">
        <v-file-input
          v-model="form.xml"
          :label="hasXml ? 'Reemplazar XML (opcional)' : 'Factura XML (opcional)'"
          accept=".xml,text/xml,application/xml"
          :rules="fileRules"
          prepend-icon=""
          prepend-inner-icon="mdi-file-code-outline"
          outlined
          dense
          show-size
        ></v-file-input>
      </v-col>
    </v-row>
    <v-alert v-if="errorMessage" type="error" dense outlined class="mb-3">{{ errorMessage }}</v-alert>
    <div class="d-flex justify-end">
      <v-btn type="submit" color="primary" depressed :loading="saving" :disabled="!valid || saving">
        {{ hasInvoice ? 'Guardar factura' : 'Registrar factura' }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import moment from 'moment';
import ClinicSettlementsService from '@/services/clinic-settlements.service';
import { settlementErrorMessage } from '@/utils/clinic-settlements.utils';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const INVOICE_NUMBER_PATTERN = /^[0-9A-Za-z-]{1,20}$/;

// Registro o corrección de la factura de la clínica (US2). El detalle actualizado que
// devuelve el backend se emite en `saved` para que el diálogo y la tabla se refresquen.
export default {
  name: 'RegisterSettlementInvoiceForm',
  props: {
    clinicId: { type: String, required: true },
    cutoffDate: { type: String, required: true },
    invoiceNumber: { type: String, default: null },
    invoiceIssuedAt: { type: String, default: null },
    // Whether the settlement already has each document: the inputs then replace it.
    hasPdf: { type: Boolean, default: false },
    hasXml: { type: Boolean, default: false }
  },
  data() {
    return {
      valid: false,
      saving: false,
      errorMessage: '',
      today: moment().format('YYYY-MM-DD'),
      form: this.initialForm(),
      invoiceNumberRules: [
        (v) => !!(v && v.trim()) || 'El N° de factura es obligatorio',
        (v) => INVOICE_NUMBER_PATTERN.test((v || '').trim()) || 'Hasta 20 caracteres: letras, números o guion'
      ],
      issuedAtRules: [
        (v) => !!v || 'La fecha de emisión es obligatoria',
        (v) => !v || v <= this.today || 'La fecha de emisión no puede ser futura'
      ],
      fileRules: [(file) => !file || file.size <= MAX_FILE_BYTES || 'El archivo supera los 10 MB']
    };
  },
  computed: {
    hasInvoice() {
      return !!this.invoiceNumber;
    }
  },
  watch: {
    invoiceNumber() {
      this.form = this.initialForm();
    },
    invoiceIssuedAt() {
      this.form = this.initialForm();
    }
  },
  methods: {
    initialForm() {
      return {
        invoiceNumber: this.invoiceNumber || '',
        invoiceIssuedAt: this.invoiceIssuedAt || '',
        pdf: null,
        xml: null
      };
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.errorMessage = '';
      const res = await ClinicSettlementsService.registerInvoice(this.clinicId, this.cutoffDate, {
        invoiceNumber: this.form.invoiceNumber.trim(),
        invoiceIssuedAt: this.form.invoiceIssuedAt,
        pdf: this.form.pdf,
        xml: this.form.xml
      });
      this.saving = false;
      if (res.status !== 'ok') {
        this.errorMessage = settlementErrorMessage(res, 'No se pudo registrar la factura. Intenta nuevamente.');
        return;
      }
      this.$emit('saved', res.data);
    }
  }
};
</script>
